<template>
    <div :class="rootClass">
        <component :is="nameTag" :class="nameClass">{{ name }}</component>
        <component
            v-if="displayOwner"
            :is="ownerTag"
            :class="ownerClass"
        >
            {{ displayOwner }}
        </component>
    </div>
</template>

<script>
/**
 * Warehouse label: `menu` = two lines (dropdown); `cell` = same two lines without
 * list padding (table cells); `field` = one line (closed v-select: name · owner).
 */
export default {
    name: "StockOptionLabel",
    props: {
        name: {
            type: String,
            default: "",
        },
        ownerName: {
            type: String,
            default: "",
        },
        /** menu: two-line list row; cell: two-line table cell; field: single-line selection */
        variant: {
            type: String,
            default: "menu",
            validator: (v) => ["menu", "cell", "field"].includes(v),
        },
    },
    computed: {
        displayOwner() {
            return (this.ownerName && String(this.ownerName).trim()) || "";
        },
        rootClass() {
            const mod =
                this.variant === "field"
                    ? "field"
                    : this.variant === "cell"
                      ? "cell"
                      : "menu";
            return ["stock-option-label", `stock-option-label--${mod}`];
        },
        nameTag() {
            return this.variant === "field" ? "span" : "div";
        },
        ownerTag() {
            return this.variant === "field" ? "span" : "div";
        },
        nameClass() {
            const v = this.variant === "field" ? "field" : this.variant === "cell" ? "cell" : "menu";
            return `stock-option-label__name stock-option-label__name--${v}`;
        },
        ownerClass() {
            const v = this.variant === "field" ? "field" : this.variant === "cell" ? "cell" : "menu";
            return `stock-option-label__owner stock-option-label__owner--${v}`;
        },
    },
};
</script>

<style scoped lang="scss">
/* Inside v-select list items, Vuetify already pads the row — no inner padding. */
.stock-option-label--menu {
    width: 100%;
    padding: 0;
    margin: 0;
}

.stock-option-label__name--menu {
    font-size: 0.8125rem;
    line-height: 1.3;
    font-weight: 500;
}

.stock-option-label__owner--menu {
    font-size: 0.6875rem;
    line-height: 1.2;
    margin-top: 4px;
    color: rgba(0, 0, 0, 0.77);
}

.theme--dark .stock-option-label__owner--menu {
    color: rgba(255, 255, 255, 0.5);
}

.stock-option-label--cell {
    width: 100%;
    padding: 0;
    margin: 0;
}

.stock-option-label__name--cell {
    font-size: 0.8125rem;
    line-height: 1.3;
    font-weight: 500;
}

.stock-option-label__owner--cell {
    font-size: 0.6875rem;
    line-height: 1.2;
    margin-top: 4px;
    color: rgba(0, 0, 0, 0.77);
}

.theme--dark .stock-option-label__owner--cell {
    color: rgba(255, 255, 255, 0.5);
}

.stock-option-label--field {
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    align-items: baseline;
    gap: 0;
    max-width: 100%;
    min-width: 0;
    overflow: hidden;
    line-height: 1.35;
}

.stock-option-label__name--field {
    flex: 0 0 auto;
    overflow: visible;
    white-space: nowrap;
    font-size: inherit;
}

.stock-option-label__owner--field {
    flex: 1 1 0%;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 0.8125rem;
    font-weight: 400;
    margin-top: 0;
    color: rgba(0, 0, 0, 0.77);
}

.stock-option-label__owner--field::before {
    content: "\00a0·\00a0";
    font-weight: 400;
    opacity: 0.55;
}

.theme--dark .stock-option-label__owner--field {
    color: rgba(255, 255, 255, 0.5);
}
</style>

<style lang="scss">
/* Portaled menus: consistent row rhythm; label has no extra horizontal inset */
.stock-option-label-v-menu.v-menu__content {
    border-radius: 4px !important;
    overflow: hidden;
}

.stock-option-label-v-menu .v-list-item__content {
    padding-top: 5px !important;
    padding-bottom: 5px !important;
}

.stock-option-label-v-menu.departure-wh-menu .departure-wh-menu__title {
    font-size: 0.8125rem !important;
    line-height: 1.35 !important;
    font-weight: 500 !important;
    white-space: normal !important;
    word-break: break-word;
}
</style>
